import { fmtInt, t } from './i18n/i18n';
import { useEffect, useRef, type ReactNode } from 'react';
import { machineById, machineLabel } from './engine/model/machines';
import { formatDuration } from './engine/stitch/stats';
import { type Tool, store, useApp } from './state/store';
import {
  copySelected,
  cutSelected,
  deleteSelected,
  deleteSelectedNode,
  duplicateSelected,
  nudge,
  paste,
  saveCurrentProject,
  select,
  selectAll,
  shiftOrder,
  startNewProject,
} from './ui/actions';
import { CanvasView } from './ui/canvas/CanvasView';
import { ZOOM_STEP, zoomAt } from './ui/canvas/zoom';
import { AccountDialog } from './ui/AccountDialog';
import { AdminDialog } from './ui/AdminDialog';
import { ExportDialog } from './ui/ExportDialog';
import {
  IconClose,
  IconColumn,
  IconEllipse,
  IconExport,
  IconFolder,
  IconHand,
  IconImport,
  IconLayers,
  IconLine,
  IconLogo,
  IconNode,
  IconPatch,
  IconPlus,
  IconPolygon,
  IconRect,
  IconRedo,
  IconRuler,
  IconSave,
  IconSelect,
  IconSliders,
  IconUndo,
  IconUser,
} from './ui/icons';
import { ImportDialog } from './ui/ImportDialog';
import { Inspector } from './ui/Inspector';
import { LayersPanel } from './ui/LayersPanel';
import { NewProjectDialog } from './ui/NewProjectDialog';
import { ProjectsDialog } from './ui/ProjectsDialog';
import { SettingsMenu } from './ui/SettingsMenu';
import { autosave } from './ui/session';
import { PatchDialog } from './ui/PatchDialog';
import { PhoneActionBar, PhoneMenu, PhoneNav, SheetBackdrop, PhoneStatus, PhoneTools, PhoneTopBar } from './ui/PhoneLayout';
import { Simulator } from './ui/Simulator';
import { usePhone } from './ui/usePhone';

const TOOLS: { id: Tool; label: string; key: string; icon: ReactNode }[] = [
  { id: 'select', label: 'Select and move', key: 'V', icon: <IconSelect size={18} /> },
  { id: 'node', label: 'Edit nodes', key: 'A', icon: <IconNode size={18} /> },
  { id: 'pan', label: 'Pan', key: 'H', icon: <IconHand size={18} /> },
  { id: 'measure', label: 'Measure', key: 'M', icon: <IconRuler size={18} /> },
  { id: 'rect', label: 'Rectangle (fill)', key: 'R', icon: <IconRect size={18} /> },
  { id: 'ellipse', label: 'Ellipse (fill)', key: 'E', icon: <IconEllipse size={18} /> },
  { id: 'polygon', label: 'Polygon (fill)', key: 'P', icon: <IconPolygon size={18} /> },
  { id: 'line', label: 'Line (running stitch)', key: 'L', icon: <IconLine size={18} /> },
  { id: 'column', label: 'Satin line', key: 'S', icon: <IconColumn size={18} /> },
];

export function App() {
  const panel = useApp((s) => s.panel);
  const mode = useApp((s) => s.mode);
  const drawer = useApp((s) => s.drawer);
  // re-render the whole UI when the language changes (texts are translated at render time)
  const lang = useApp((s) => s.lang);
  const phone = usePhone();
  useShortcuts();
  useEffect(() => {
    // Work is autosaved; only warn if the last change has not been written yet.
    const warn = (e: BeforeUnloadEvent) => {
      if (autosave.pending || autosave.error) {
        void autosave.flush();
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);
  return (
    <div key={lang} className={'app' + (phone ? ' phone' : '') + (drawer === 'inspector' ? ' inspector-open' : '') + (drawer === 'layers' ? ' layers-open' : '')}>
      {phone ? <PhoneTopBar nameInput={<ProjectNameInput />} /> : <TopBar />}
      {phone ? mode === 'edit' && <PhoneTools tools={TOOLS} /> : <Toolbox />}
      <LayersPanel />
      <main style={{ gridArea: 'stage', position: 'relative', minWidth: 0, minHeight: 0, display: 'flex' }}>{mode === 'edit' ? <CanvasView /> : <Simulator />}<StageOverlay phone={phone} />{phone && mode === 'edit' && <PhoneActionBar />}</main>
      <Inspector />
      {phone ? (
        <>
          <PhoneStatus />
          <SheetBackdrop />
          <PhoneNav />
          {drawer === 'menu' && <PhoneMenu />}
        </>
      ) : (
        <StatusBar />
      )}
      {panel === 'import' && <ImportDialog />}
      {panel === 'export' && <ExportDialog />}
      {panel === 'projects' && <ProjectsDialog />}
      {panel === 'patch' && <PatchDialog />}
      {panel === 'new' && <NewProjectDialog />}
      {panel === 'account' && <AccountDialog />}
      {panel === 'admin' && <AdminDialog />}
      <Toasts />
    </div>
  );
}

const saveWithToast = () => void saveCurrentProject().catch((e) => store.toast('error', String(e?.message ?? e)));

/** The project (and design) name; Enter saves, Escape restores the name before editing. */
function ProjectNameInput() {
  const project = useApp((s) => s.project);
  // name before editing started, restored on Escape
  const nameBefore = useRef({ name: project.name, dirty: project.dirty });
  return (
    <input
      className="project-name"
      aria-label={t('Project name')}
      value={project.name}
      title={t('Enter saves · Esc cancels')}
      onFocus={() => (nameBefore.current = { name: project.name, dirty: project.dirty })}
      onChange={(e) => store.set((s) => ({ project: { ...s.project, name: e.target.value, dirty: true } }))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
          saveWithToast();
        } else if (e.key === 'Escape') {
          const before = nameBefore.current;
          store.set((s) => ({ project: { ...s.project, ...before } }));
          e.currentTarget.blur();
        }
      }}
    />
  );
}

function TopBar() {
  const project = useApp((s) => s.project);
  const mode = useApp((s) => s.mode);
  const layers = useApp((s) => s.layers);
  const canUndo = useApp((s) => s.history.past.length > 0);
  const canRedo = useApp((s) => s.history.future.length > 0);
  const hasImage = useApp((s) => !!s.image);
  const setLayer = (k: keyof typeof layers) => store.set((s) => ({ layers: { ...s.layers, [k]: !s.layers[k] } }));
  return (
    <header className="topbar">
      <div className="brand">
        <IconLogo size={22} /> <span className="brand-text">StitchCraft</span>
      </div>
      <ProjectNameInput />
      {project.dirty && <span className="dirty-dot" title={t('Unsaved changes')} aria-label={t('Unsaved changes')} />}
      <button className="btn ghost" aria-label={t('New project')} title={t('New project') + ' (Alt+N)'} onClick={() => startNewProject()}>
        <IconPlus size={16} /> <span className="btn-text">{t('New')}</span>
      </button>
      <button className="btn ghost" aria-label={t('Projects')} onClick={() => store.set({ panel: 'projects' })} title={t('Projects') + ' (Ctrl+O)'}>
        <IconFolder size={16} /> <span className="btn-text">{t('Projects')}</span>
      </button>
      <button className="btn ghost icon" aria-label={t('Save') + ' (Ctrl+S)'} title={t('Save locally') + ' (Ctrl+S)'} onClick={saveWithToast}>
        <IconSave size={16} />
      </button>
      <span className="sep" />
      <button className="btn ghost icon" aria-label={t('Undo') + ' (Ctrl+Z)'} title={t('Undo') + ' (Ctrl+Z)'} disabled={!canUndo} onClick={() => store.undo()}>
        <IconUndo size={16} />
      </button>
      <button className="btn ghost icon" aria-label={t('Redo') + ' (Ctrl+Y)'} title={t('Redo') + ' (Ctrl+Shift+Z)'} disabled={!canRedo} onClick={() => store.redo()}>
        <IconRedo size={16} />
      </button>
      <span className="sep" />
      <div className="segmented" role="group" aria-label={t('View')}>
        <button aria-pressed={mode === 'edit'} onClick={() => store.set({ mode: 'edit' })}>
          {t('Edit')}
        </button>
        <button aria-pressed={mode === 'simulate'} onClick={() => store.set({ mode: 'simulate', pick: null })}>
          {t('Sew simulation')}
        </button>
      </div>
      {mode === 'edit' && (
        <div className="segmented" role="group" aria-label={t('Canvas layers')}>
          <button aria-pressed={layers.image} disabled={!hasImage} onClick={() => setLayer('image')} title={t('Original image')}>
            {t('Image')}
          </button>
          <button aria-pressed={layers.shapes} onClick={() => setLayer('shapes')} title={t('Editable shapes')}>
            {t('Shapes')}
          </button>
          <button aria-pressed={layers.stitches} onClick={() => setLayer('stitches')} title={t('Generated stitches')}>
            {t('Stitches')}
          </button>
          <button aria-pressed={layers.jumps} onClick={() => setLayer('jumps')} title={t('Jumps and trims')}>
            {t('Jumps')}
          </button>
        </div>
      )}
      <span className="spacer" />
      <button className="btn only-narrow icon" aria-label={t('Object list')} onClick={() => store.set((s) => ({ drawer: s.drawer === 'layers' ? null : 'layers' }))}>
        <IconLayers />
      </button>
      <button className="btn only-narrow icon" aria-label={t('Properties')} onClick={() => store.set((s) => ({ drawer: s.drawer === 'inspector' ? null : 'inspector' }))}>
        <IconSliders />
      </button>
      <AccountButton />
      <button className="btn" aria-label={t('Make patch')} title={t('Make a patch (badge) around the design')} onClick={() => store.set({ panel: 'patch' })}>
        <IconPatch size={16} /> <span className="btn-text">{t('Patch')}</span>
      </button>
      <button className="btn" aria-label={t('Import image')} onClick={() => store.set({ panel: 'import' })}>
        <IconImport size={16} /> <span className="btn-text">{t('Import image')}</span>
      </button>
      <button className="btn primary" aria-label={t('Export')} onClick={() => store.set({ panel: 'export' })}>
        <IconExport size={16} /> <span className="btn-text">{t('Export')}</span>
      </button>
    </header>
  );
}

/** Shown only when the server offers accounts, so the static app looks exactly as before. */
function AccountButton() {
  const account = useApp((s) => s.account);
  if (!account.enabled) return null;
  return (
    <button className="btn ghost" aria-label={account.user ? t('Account: {email}', { email: account.user.email }) : t('Log in')} title={account.user?.email} onClick={() => store.set({ panel: 'account' })}>
      <IconUser size={16} /> <span className="btn-text">{account.user ? account.user.email.split('@')[0] : t('Log in')}</span>
    </button>
  );
}

function Toolbox() {
  const tool = useApp((s) => s.tool);
  const mode = useApp((s) => s.mode);
  return (
    <div className="tools" role="toolbar" aria-label={t('Tools')} aria-orientation="vertical">
      {TOOLS.map((tl, i) => (
        <span key={tl.id} style={{ display: 'contents' }}>
          {i === 3 && <hr />}
          <button
            className="tool"
            aria-pressed={tool === tl.id}
            aria-label={`${t(tl.label)} (${tl.key})`}
            title={`${t(tl.label)} (${tl.key})`}
            disabled={mode !== 'edit'}
            onClick={() => store.set({ tool: tl.id, pick: null })}
          >
            {tl.icon}
            <span className="kbd">{tl.key}</span>
          </button>
        </span>
      ))}
      <span className="spacer" />
      <SettingsMenu />
    </div>
  );
}

function StageOverlay({ phone }: { phone: boolean }) {
  const pending = useApp((s) => s.stitch.pending);
  const error = useApp((s) => s.stitch.error);
  const empty = useApp((s) => s.history.present.objects.length === 0 && !s.image);
  const mode = useApp((s) => s.mode);
  // the start card gives way as soon as a drawing tool is chosen, and can be closed
  const showStart = useApp((s) => !s.startCardClosed && s.tool === 'select');
  return (
    <>
      {mode === 'edit' && (
        <div className="overlay-tl" style={{ left: 'auto', right: 10 }}>
          {pending && <span className="chip busy">{t('Generating stitches…')}</span>}
          {error && <span className="chip" style={{ color: 'var(--err)' }}>{t('Stitch error: {error}', { error })}</span>}
        </div>
      )}
      {empty && showStart && mode === 'edit' && (
        <div className="stage-empty">
          <div
            className="card"
            onWheel={(e) => {
              // let the canvas zoom even when the pointer is over this card
              document.querySelector('.stage > svg')?.dispatchEvent(new WheelEvent('wheel', { deltaY: e.deltaY, deltaX: e.deltaX, deltaMode: e.deltaMode, clientX: e.clientX, clientY: e.clientY, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, bubbles: true }));
            }}
          >
            <button className="btn icon ghost card-close" aria-label={t('Hide this tip')} title={t('Hide this tip')} onClick={() => store.set({ startCardClosed: true })}>
              <IconClose size={14} />
            </button>
            <h2>{t('Start a new embroidery design')}</h2>
            <p>{phone ? t('Import an image (PNG, JPEG or SVG) for automatic digitizing, or draw shapes yourself with the tools below.') : t('Import an image (PNG, JPEG or SVG) for automatic digitizing, or draw shapes yourself with the tools on the left.')}</p>
            <div className="row" style={{ justifyContent: 'center' }}>
              <button className="btn primary" onClick={() => store.set({ panel: 'import' })}>
                <IconImport size={16} /> {t('Import image')}
              </button>
              <button className="btn" onClick={() => store.set({ tool: 'rect' })}>
                {t('Draw a shape')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StatusBar() {
  const result = useApp((s) => s.stitch.result);
  const hoop = useApp((s) => s.history.present.machine.hoop);
  const machineId = useApp((s) => s.history.present.machine.machineId);
  const machine = machineById(machineId);
  const hiddenCount = useApp((s) => s.history.present.objects.filter((o) => !o.visible).length);
  const st = result?.stats;
  const errors = result?.issues.filter((i) => i.severity === 'error').length ?? 0;
  const warnings = result?.issues.filter((i) => i.severity === 'warning').length ?? 0;
  return (
    <footer className="statusbar" aria-label={t('Design statistics')}>
      {st ? (
        <>
          <span className="stat">
            {t('Stitches')} <b>{fmtInt(st.stitches)}</b>
          </span>
          <span className="stat">
            {t('Size')} <b>{st.widthMm.toFixed(1)} × {st.heightMm.toFixed(1)} mm</b>
          </span>
          <span className="stat">
            {t('Colours')} <b>{st.uniqueColors}</b>
          </span>
          <span className="stat">
            {t('Colour changes')} <b>{st.colorChanges}</b>
          </span>
          <span className="stat">
            {t('Jumps')} <b>{st.jumps}</b>
          </span>
          <span className="stat">
            {t('Trims')} <b>{st.trims}</b>
          </span>
          <span className="stat" title={t('Estimate based on the machine settings')}>
            {t('Sewing time')} ≈ <b>{formatDuration(st.estimatedSeconds)}</b> ({t('estimate')})
          </span>
          <span className="stat" title={machine ? machineLabel(machine) : undefined}>
            {t('Hoop')} <b>{t(hoop.name)}</b>
          </span>
          <span className="spacer" />
          {hiddenCount > 0 && (
            <span className="badge warn" title={t('Hidden objects are not sewn and are not included in the export')}>
              {t('{n} hidden – not sewn', { n: hiddenCount })}
            </span>
          )}
          {errors > 0 && (
            <button className="badge err" onClick={() => store.set({ panel: 'export' })}>
              {t('{n} error(s)', { n: errors })}
            </button>
          )}
          {warnings > 0 && <span className="badge warn">{t('{n} warning(s)', { n: warnings })}</span>}
          {!errors && !warnings && st.stitches > 0 && <span className="badge ok">{t('Ready for export')}</span>}
          <span>{result.ms.toFixed(0)} ms</span>
        </>
      ) : (
        <span>{t('No stitches yet')}</span>
      )}
    </footer>
  );
}

function Toasts() {
  const toasts = useApp((s) => s.toasts);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast ${toast.kind}`}>
          <p>{toast.message}</p>
          <button className="btn small icon ghost" aria-label={t('Close message')} onClick={() => store.dismissToast(toast.id)}>
            <IconClose size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

function useShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target;
      if (t instanceof Element && t.closest('input, textarea, select, [contenteditable="true"]')) return;
      const st = store.getState();
      if (st.panel) return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (mod && k === 'z') {
        e.preventDefault();
        if (e.shiftKey) store.redo();
        else store.undo();
      } else if (mod && k === 'y') {
        e.preventDefault();
        store.redo();
      } else if (mod && k === 's') {
        e.preventDefault();
        void saveCurrentProject().catch((err) => store.toast('error', String(err?.message ?? err)));
      } else if (e.altKey && !mod && k === 'n') {
        // Ctrl+N is reserved by the browser (new window)
        e.preventDefault();
        startNewProject();
      } else if (mod && k === 'o') {
        e.preventDefault();
        store.set({ panel: 'projects' });
      } else if (mod && k === 'e') {
        e.preventDefault();
        store.set({ panel: 'export' });
      } else if (st.mode !== 'edit') {
        return;
      } else if (mod && k === 'd') {
        e.preventDefault();
        duplicateSelected();
      } else if (mod && k === 'c') copySelected();
      else if (mod && k === 'x') cutSelected();
      else if (mod && k === 'v') paste();
      else if (mod && k === 'a') {
        e.preventDefault();
        selectAll();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!deleteSelectedNode()) deleteSelected();
      } else if (e.key === 'Escape') {
        if (st.pick) store.set({ pick: null });
        else if (st.tool !== 'select') store.set({ tool: 'select' });
        else select([]);
      } else if (e.key.startsWith('Arrow') && st.selection.length) {
        e.preventDefault();
        if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
          shiftOrder(e.key === 'ArrowUp' ? -1 : 1);
          return;
        }
        const d = e.shiftKey ? 5 : 0.5;
        nudge(e.key === 'ArrowLeft' ? -d : e.key === 'ArrowRight' ? d : 0, e.key === 'ArrowUp' ? -d : e.key === 'ArrowDown' ? d : 0);
      } else if (!mod && !e.altKey) {
        const tool = TOOLS.find((x) => x.key.toLowerCase() === k);
        if (tool) store.set({ tool: tool.id, pick: null });
        else if (k === '0' || k === 'f') store.set((s) => ({ fitNonce: s.fitNonce + 1 }));
        else if (k === '+' || k === '=') store.set((s) => ({ viewport: zoomCenter(s.viewport, ZOOM_STEP) }));
        else if (k === '-') store.set((s) => ({ viewport: zoomCenter(s.viewport, 1 / ZOOM_STEP) }));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

function zoomCenter(v: { zoom: number; panX: number; panY: number }, f: number) {
  const stage = document.querySelector('.stage');
  return zoomAt(v, f, (stage?.clientWidth ?? 800) / 2, (stage?.clientHeight ?? 600) / 2);
}
